import React, { useEffect, useState } from 'react';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useAtomValue, useSetAtom } from 'jotai';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { userReactionAtom } from '../../../data/atoms/reactionAtom';
import api from '../../../data/api';
import toast from 'react-hot-toast';
import TRC_CONTENT from '@/constants/trcContentType';
import FeedbackModal from '@/components/VideosFeedbackModal/FeedbackModal';
import { videoReactions } from '@/constants/videoReactions';

const getLocalReaction = videoId => {
  if (!videoId) return '';
  try {
    return localStorage.getItem(`promoReaction:${videoId}`) || '';
  } catch (error) {
    return '';
  }
};

const VideoReaction = ({ videoId, type = TRC_CONTENT.VIDEO, localOnly = false }) => {
  const userData = useAtomValue(userInfoAtom);
  const reactionMap = useAtomValue(userReactionAtom);
  const setReactionMap = useSetAtom(userReactionAtom);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [localReaction, setLocalReaction] = useState(() => getLocalReaction(videoId));

  useEffect(() => {
    if (localOnly) {
      setLocalReaction(getLocalReaction(videoId));
    }
  }, [localOnly, videoId]);

  const currentReaction = localOnly ? localReaction : reactionMap[videoId];
  const isLiked = currentReaction === videoReactions.LIKE;
  const isDisliked = currentReaction === videoReactions.DISLIKE;

  const handleAddReaction = async (reactionType, feedback = '') => {
    if (localOnly) {
      const nextReaction = localReaction === reactionType ? '' : reactionType;
      setLocalReaction(nextReaction);
      try {
        if (nextReaction) {
          localStorage.setItem(`promoReaction:${videoId}`, nextReaction);
        } else {
          localStorage.removeItem(`promoReaction:${videoId}`);
        }
      } catch (error) {
        console.error('Error saving local reaction:', error);
      }
      return;
    }

    try {
      await api.addReactionApi(userData?.user.userId, videoId, reactionType, type, feedback);
      setReactionMap(prev => {
        const updated = { ...prev };
        if (prev[videoId] === reactionType) {
          delete updated[videoId];
        } else {
          updated[videoId] = reactionType;
        }
        return updated;
      });
    } catch (error) {
      toast.error('An error occurred, please login again' ,{position:"top-center", duration: 3000})
      console.error("Error sending reaction:", error);
    }
  };

  const handleLikeClick = () => {
    handleAddReaction(videoReactions.LIKE);
  };

  const handleDislikeClick = () => {
    if (localOnly) {
      handleAddReaction(videoReactions.DISLIKE);
      return;
    }

    if (isDisliked) {
      handleAddReaction(videoReactions.DISLIKE);
    } else {
      setShowFeedbackModal(true);
    }
  };

  const handleFeedbackSubmit = (feedback) => {
    handleAddReaction(videoReactions.DISLIKE, feedback);
    setShowFeedbackModal(false);
  };

  return (
    <>
      <div className='reaction-section'>
        <div className='like-section' onClick={handleLikeClick}>
          <div className='icon-bg' style={{ 
            border: isLiked ? 'none' : '0.063rem solid white', 
            backgroundColor: isLiked ? 'white' : 'transparent' 
          }}>
            {isLiked ? (
              <ThumbUpIcon style={{color: '#0B6386' }}/>
            ) : (
              <ThumbUpOffAltIcon style={{ color: isLiked ? '#0B6386' : 'white' }} />
            )}
          </div>
          <p style={{ color: 'white' }} className='reaction-text'>
            {isLiked ? 'Liked' : 'Like'}
          </p>
        </div>

        <div className='vertical-divider' />

        <div className='dislike-section' onClick={handleDislikeClick}>
          <div className='icon-bg' style={{ 
            border: isDisliked ? 'none' : '0.063rem solid white', 
            backgroundColor: isDisliked ? 'white' : 'transparent'  
          }}>
            {isDisliked ? (
              <ThumbDownIcon style={{color: '#0B6386' }}/>
            ) : (
              <ThumbDownOffAltIcon style={{ color:'white' }} />
            )}
          </div>
          <p style={{ color: 'white' }} className='reaction-text'>
            {isDisliked ? 'Disliked' : 'Dislike'}
          </p>
        </div>
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );
};

export default VideoReaction;
